import { render } from 'preact';
import type { CSSProperties } from 'preact/compat';

import { Widget as WidgetComponent } from '@/components/Widget';
import { CustomerLoyaltyProvider } from '@/context/LoyaltyContext';
import { PlatformContext } from '@/context/PlatformContext';
import { WidgetConfigProvider } from '@/context/WidgetConfigContext';
import { COLORS } from '@/helpers/constants/theme';
import type { I_PlatformHandlers } from '@/types/Platforms';
import type { I_WidgetConfig } from '@/types/Widget';

/**
 * Renders the widget component tree into the specified root element
 * @param rootElement - The root element to render into
 * @param platformHandler - The platform handler for the widget
 * @param config - The widget configuration
 */
export function renderWidget(
  rootElement: HTMLElement,
  platformHandler: I_PlatformHandlers,
  config: I_WidgetConfig,
): void {
  render(
    <PlatformContext.Provider value={platformHandler}>
      <CustomerLoyaltyProvider>
        <WidgetConfigProvider
          initialConfig={{
            widgetLocation: config?.widgetLocation,
            mobileMessageLocation: config?.mobileMessageLocation,
            messageLocation: config?.messageLocation,
            platform: config?.platform,
            customer: config?.customer,
            storefrontLoginURL: config?.storefrontLoginURL,
            theme: config?.theme,
            appName: config?.appName,
            buyNowButtonSelector: config?.buyNowButtonSelector,
            showNationality: config?.showNationality,
            fullRedemptionOnly: config?.fullRedemptionOnly,
            showMessages: config?.showMessages,
          }}
        >
          <div
            style={
              {
                '--widget-heading-color': config.theme?.headingColor ?? COLORS.BRAND_PRIMARY,
                '--widget-body-color': config.theme?.bodyColor ?? COLORS.BRAND_PRIMARY,
              } as CSSProperties
            }
          >
            <WidgetComponent />
          </div>
        </WidgetConfigProvider>
      </CustomerLoyaltyProvider>
    </PlatformContext.Provider>,
    rootElement,
  );
}

/**
 * Unmounts the widget component tree from the specified root element
 * @param rootElement - The root element to unmount from
 */
export function unmountWidget(rootElement: HTMLElement): void {
  render(null, rootElement);
}
